import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { useLoading } from "../../context/LoadingContext";
import { autoDisplayValue } from "../../commons/FormatValue";
import { getServiceRequest } from "../../commons/ServiceRequest";
import { initializeSetOption } from "../../utils/InitializeSetDefault";
import {
  FloatingControlComponent,
  SearchSelectComponent
} from "../FormComponent";
import TableForm from "../TableForm";
import ConfirmModal from "../ConfirmModal";
import CustomersService from "../../services/customer.service";
import CardDisplayComponent from "../CardDisplayComponent";
import { emptyValueObjects } from "../../utils/ManageArray";

const OperatingProceduresCustomerForm = ({
  searchValue,
  editData,
  onSubmitCallback
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [customers, setCustomers] = useState([]);
  const defaultData = {
    Item_Step_Id: searchValue?.StepDes[0]?.value,
    Item_Std_Des: "",
    Con_Spec_Hd_Id: null,
    Cus_Id: "",
    Priority: "",
    Item_Std_Skip1: "",
    Item_Std_Skip2: "",
    Com_Code: user.Com_Code
  };
  const [data, setData] = useState({ Array: [defaultData] });
  const rowData = data.Array?.[0];
  const searchInput = [
    {
      name: "Cus_Id",
      label: "รหัสลูกค้า (BP code)",
      type: "select",
      size: 3,
      filterBy: ["Cus_Code"],
      options: initializeSetOption({
        key: "Id",
        label: "Cus_Code",
        dataset: customers
      }),
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest: CustomersService.getAllCustomers(query),
          callback: ({ items }) => setCustomers(items)
        })
    },
    {
      name: "Cus_NumBranch",
      label: "รหัสสาขา",
      type: "text",
      size: 2,
      findValue: { data: customers, key: "Id", value: rowData?.Cus_Id }
    },
    {
      name: "Cus_Name",
      label: "ชื่อลูกค้า/สาขา",
      type: "text",
      size: 5,
      findValue: { data: customers, key: "Id", value: rowData?.Cus_Id }
    }
  ];
  const inputField = [
    {
      name: "Item_Std_Des",
      label: "ขั้นตอนการปฏิบัติงานเพิ่มเติม",
      type: "text"
    },
    { name: "Priority", label: "Priority", type: "text", size: 2 },
    {
      name: "Item_Std_Skip1",
      label: "เงื่อนไขการข้าม1",
      type: "select",
      size: 2,
      noneAsync: true,
      options: [
        { value: true, label: "Y" },
        { value: false, label: "N" }
      ]
    },
    {
      name: "Item_Std_Skip2",
      label: "เงื่อนไขการข้าม2",
      type: "select",
      size: 2,
      noneAsync: true,
      options: [
        { value: true, label: "Y" },
        { value: false, label: "N" }
      ]
    }
  ];

  const checkValidity = () => {
    if (emptyValueObjects(data.Array, ["Con_Spec_Hd_Id"]).length > 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      if (!editData) onSubmitCallback(dataCallback);
      else onSubmitCallback(dataCallback.Array[0]);
    }
  };

  useEffect(() => {
    if (editData) {
      getServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: CustomersService.getAllCustomers(editData.Cus_Id),
        callback: ({ items }) => setCustomers(items)
      });
      setData({
        Array: [
          {
            Item_Step_Id: editData.Item_Step_Id,
            Item_Std_Des: editData.Item_Std_Des,
            Con_Spec_Hd_Id: editData.Con_Spec_Hd_Id,
            Cus_Id: editData.Cus_Id,
            Priority: editData.Priority,
            Item_Std_Skip1: editData.Item_Std_Skip1,
            Item_Std_Skip2: editData.Item_Std_Skip2,
            Com_Code: editData.Com_Code
          }
        ]
      });
    }
  }, [editData]);

  return (
    <Form noValidate validated={validated} onSubmit={handleValidated}>
      <CardDisplayComponent
        body={{
          as: Row,
          className: "justify-content-center",
          element: searchInput.map(
            ({
              name,
              label,
              type,
              size,
              options,
              filterBy,
              queryService,
              findValue
            }) => (
              <Col key={name} sm={size}>
                {type === "select" ? (
                  <SearchSelectComponent
                    labelField={label}
                    options={options}
                    placeholder="โปรดเลือก"
                    disabled={!!editData}
                    isInvalid={rowData?.[name] === ""}
                    filterBy={filterBy}
                    queryService={queryService}
                    onChange={(change) =>
                      setData((prev) => ({
                        ...prev,
                        Array: [
                          { ...prev.Array[0], [name]: change?.value },
                          ...prev.Array.slice(1)
                        ]
                      }))
                    }
                    value={rowData?.[name]}
                  />
                ) : (
                  <FloatingControlComponent
                    readOnly
                    type="text"
                    name={name}
                    label={label}
                    disabled={!!editData}
                    value={autoDisplayValue({ name, findValue })}
                  />
                )}
              </Col>
            )
          )
        }}
      />

      <CardDisplayComponent
        body={{
          element: (
            <TableForm
              data={data.Array}
              columns={inputField}
              editForm={!!editData}
              defaultData={defaultData}
              onChangeCallback={(newData) =>
                setData({
                  Array: newData.map(({ Priority, ...item }) => ({
                    ...item,
                    Cus_Id: Number(rowData?.Cus_Id),
                    Priority: Number(Priority)
                  }))
                })
              }
            />
          )
        }}
        footer={{
          className: "text-end",
          element: (
            <>
              <Button
                type="submit"
                variant="success"
                className="px-5 py-2 text-dark rounded-pill"
              >
                {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
              </Button>
              <ConfirmModal
                show={showModal}
                data={data}
                content={editData ? "edit" : "save"}
                onSubmitCallback={handleSubmit}
              />

              <Button
                variant="outline-success"
                className="px-5 py-2 text-dark border-secondary rounded-pill"
                onClick={() =>
                  navigate("/wo_master/request", { state: searchValue })
                }
              >
                ยกเลิก
              </Button>
            </>
          )
        }}
      />
    </Form>
  );
};

OperatingProceduresCustomerForm.propTypes = {
  searchValue: PropTypes.object.isRequired,
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default OperatingProceduresCustomerForm;
